import React, { useState, useRef, useImperativeHandle, forwardRef } from "react";
import { Form, Button, Upload, Tooltip, Tag, message } from "antd";
import { QuestionCircleOutlined, UploadOutlined, PaperClipOutlined } from "@ant-design/icons";
import { API_TXALERT } from "../../config/config";
import ToBase64 from "../../helper/ToBase64";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import CreditDocumentTooltip from "../../helper/CreditDocumentTooltip";
import formatFileSize from "../../helper/FormatFileSize";
import { normalizeFilename, showDuplicateError, resultMessage } from "../../helper/CreditDocumentDuplicate";
import { mismatchedAlertId, alertIdMismatchMessage, showAlertIdMismatchWarning } from "../../helper/CreditDocumentAlertId";

// Base64 inflates payload by ~33%, so 5MB raw ≈ 6.7MB base64 - stays under the server's 8MB post_max_size limit.
const MAX_TOTAL_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_SIZE_MB = MAX_TOTAL_SIZE_BYTES / (1024 * 1024);
// Fixed width for the Remove link so the total line stays aligned with the file sizes in every language.
const REMOVE_COLUMN_WIDTH = 64;

const CreditDocumentUploadField = forwardRef((props, ref) => {
    const { t, required, alertId, onChange } = props;
    const [uploadFiles, setUploadFiles] = useState([]);
    const totalSizeRef = useRef(0);
    // Mirrors the selected filenames so a multi-file drop sees files added earlier in the same drop.
    const namesRef = useRef(new Set());

    useImperativeHandle(ref, () => ({
        getFiles: () => uploadFiles,
        hasFiles: () => uploadFiles.length > 0,
        reset: () => { setUploadFiles([]); totalSizeRef.current = 0; namesRef.current = new Set(); if (onChange) onChange([]); },
        uploadDocuments: async (filesToUpload) => {
            const files = filesToUpload || uploadFiles;
            if (files.length === 0) return true;
            try {
                const documents = await Promise.all(
                    files.map(async (file) => {
                        const base64Full = await ToBase64(file);
                        const content = base64Full.replace(/^data:[^;]+;base64,/, "");
                        return {
                            filename: file.name,
                            filetype: file.type || "application/octet-stream",
                            content: content,
                        };
                    })
                );

                const uploadResponse = await axios.post(
                    API_TXALERT + "/credit/documents/upload",
                    { alert_id: alertId, documents: documents },
                    { headers: { Authorization: "Bearer " + Cookies.get("access_token") } }
                );

                if (uploadResponse.data.status === "error" || uploadResponse.data.status === "EXC") {
                    const errMsg = uploadResponse.data.error_msg?.message || uploadResponse.data.message || "Unknown error";
                    Swal.fire({
                        icon: "warning",
                        title: t("contents.alerts.document_upload_failed") || "Document upload failed",
                        text: errMsg + ". " + (t("contents.alerts.document_upload_retry_hint") || "Please upload documents again from the alert details page."),
                        showConfirmButton: true,
                    });
                    return false;
                }
                const failed = (uploadResponse.data.results || []).filter((r) => !r.status);
                if (failed.length > 0) {
                    Swal.fire({
                        icon: "warning",
                        title: t("contents.alerts.document_upload_failed") || "Document upload failed",
                        html: failed
                            .map((r) => `${r.filename}: ${resultMessage(t, r)}`.replace(/</g, "&lt;").replace(/>/g, "&gt;"))
                            .join("<br/>"),
                        showConfirmButton: true,
                    });
                    return false;
                }
                return true;
            } catch (error) {
                const errMsg = error?.response?.data?.error_msg?.message || error?.response?.data?.message || error.message || "Unknown error";
                Swal.fire({
                    icon: "warning",
                    title: t("contents.alerts.document_upload_failed") || "Document upload failed",
                    text: errMsg + ". " + (t("contents.alerts.document_upload_retry_hint") || "Please upload documents again from the alert details page."),
                    showConfirmButton: true,
                });
                return false;
            }
        },
    }), [uploadFiles, alertId, t, onChange]);

    const totalSizeBytes = uploadFiles.reduce((sum, f) => sum + f.size, 0);

    return (
        <Form.Item
            className={props.className}
            required={required}
            label={props.label}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Upload
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    multiple
                    showUploadList={false} // file list rendered manually below to keep the info icon row height stable
                    beforeUpload={(file) => {
                        const name = normalizeFilename(file.name);
                        if (namesRef.current.has(name)) {
                            showDuplicateError(t, file.name);
                            return false;
                        }
                        const newTotal = totalSizeRef.current + file.size;
                        if (newTotal > MAX_TOTAL_SIZE_BYTES) {
                            const usedMB = (totalSizeRef.current / (1024 * 1024)).toFixed(2);
                            message.error(
                                `Total file size would exceed ${MAX_TOTAL_SIZE_MB}MB (currently ${usedMB}MB used). Please remove some files before adding more.`,
                                5
                            );
                            return false;
                        }
                        totalSizeRef.current = newTotal;
                        namesRef.current.add(name);
                        const fileAlertId = mismatchedAlertId(file.name, alertId);
                        if (fileAlertId) showAlertIdMismatchWarning(t, file.name, fileAlertId);
                        setUploadFiles((prev) => {
                            const next = [...prev, file];
                            if (onChange) onChange(next);
                            return next;
                        });
                        return false;
                    }}
                >
                    <Button icon={<UploadOutlined />}>{t("contents.alerts.upload_credit_documents") || "Upload Credit Documents"}</Button>
                </Upload>
                <Tooltip title={<CreditDocumentTooltip t={t} />} overlayStyle={{ maxWidth: 400 }}>
                    <QuestionCircleOutlined style={{ color: "#999", cursor: "pointer" }} />
                </Tooltip>
            </div>
            <div style={{ marginTop: 4, color: "#999", fontSize: 12 }}>
                {t("contents.alerts.upload_documents_hint") || `Supported file types: PDF, DOC, DOCX, PNG, JPG, JPEG. Maximum combined file size: ${MAX_TOTAL_SIZE_MB} MB.`}
            </div>
            {uploadFiles.map((file, index) => (
                <div
                    key={file.uid}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "4px 0",
                        gap: 6,
                        borderTop: index > 0 ? "1px solid #f0f0f0" : undefined,
                    }}
                >
                    <PaperClipOutlined style={{ color: "#999", flexShrink: 0 }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14 }}>
                        {file.name}
                    </span>
                    {mismatchedAlertId(file.name, alertId) && (
                        <Tag color="warning" style={{ marginRight: 0, flexShrink: 0 }}>
                            {alertIdMismatchMessage(t, mismatchedAlertId(file.name, alertId))}
                        </Tag>
                    )}
                    <span style={{ color: "#999", fontSize: 12, whiteSpace: "nowrap", flexShrink: 0, marginLeft: "auto" }}>
                        ({formatFileSize(file.size)})
                    </span>
                    <Button
                        type="link"
                        danger
                        size="small"
                        style={{ width: REMOVE_COLUMN_WIDTH, padding: 0, textAlign: "right", flexShrink: 0 }}
                        onClick={() => {
                            setUploadFiles((prev) => {
                                const next = prev.filter((f) => f.uid !== file.uid);
                                totalSizeRef.current = next.reduce((sum, f) => sum + f.size, 0);
                                namesRef.current = new Set(next.map((f) => normalizeFilename(f.name)));
                                if (onChange) onChange(next);
                                return next;
                            });
                        }}
                    >
                        {t("tables.actions.remove") || "Remove"}
                    </Button>
                </div>
            ))}
            {uploadFiles.length > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", padding: `4px ${REMOVE_COLUMN_WIDTH + 6}px 0 0`, borderTop: "1px solid #f0f0f0", fontSize: 12 }}>
                    <span style={{ color: totalSizeBytes > MAX_TOTAL_SIZE_BYTES ? "#ff4d4f" : "#52c41a", fontWeight: 500 }}>
                        {t("contents.alerts.total_size") || "Total:"} {formatFileSize(totalSizeBytes)} / {MAX_TOTAL_SIZE_MB}MB
                    </span>
                </div>
            )}
        </Form.Item>
    );
});

export default CreditDocumentUploadField;
